import { AccountData, AccountLocationData, OrderPageData } from '../../api';
import { PartialLabelData } from '../../api/models/PartialLabelData';
import { LocationsStatus } from '../../api/models/LocationsStatus';

export type LocationGroup = {
  locationId: string | null;
  locationName: string;
  pages: OrderPageData[];
};

export const getActiveLocations = (account: AccountData) =>
  (account.locations ?? []).filter((l) => l.status !== LocationsStatus.ARCHIVED);

export const isMultiLocation = (account: AccountData) => getActiveLocations(account).length > 1;

export const getLocationName = (location: AccountLocationData, language: keyof PartialLabelData): string =>
  location.name?.[language] || location.name?.en || location.address?.[language] || location.address?.en || '';

export const getEffectivePageLocationId = (
  pageLocationId: string | undefined | null,
  account: AccountData,
): string | null => getActiveLocations(account).find((location) => location.id === pageLocationId)?.id ?? null;

export const groupPagesByLocation = (
  pages: OrderPageData[],
  account: AccountData,
  language: keyof PartialLabelData,
): LocationGroup[] => {
  if (getActiveLocations(account).length <= 1) {
    return [{ locationId: null, locationName: '', pages }];
  }

  const activeLocations = getActiveLocations(account);
  const defaultLocation = activeLocations.find((l) => l.default) ?? activeLocations[0];
  const groupMap = new Map<string, LocationGroup>();

  for (const page of pages) {
    const activeLoc = activeLocations.find((l) => l.id === page.location_id) ?? defaultLocation;
    const key = activeLoc?.id ?? '';
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        locationId: key || null,
        locationName: activeLoc?.name?.[language] ?? activeLoc?.name?.en ?? '',
        pages: [],
      });
    }
    groupMap.get(key)!.pages.push(page);
  }

  // Named location groups first (alphabetical), unlabelled group last
  return Array.from(groupMap.values()).sort((a, b) => {
    if (!a.locationName) return 1;
    if (!b.locationName) return -1;
    return a.locationName.localeCompare(b.locationName);
  });
};
